// ABOUTME: App-wide host of the bulk change dialog: starts the scan, apply and cancel jobs through the REST API.
// ABOUTME: Registers each job for a completion snackbar whose action reopens the dialog on the run.

import useJobSubscriptionContext from '@src/store/JobSubscriptionContext/JobSubscriptionContext.hook';
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
    cancelFindReplaceRun,
    createFindReplaceRun,
    FindReplaceApiError,
    startFindReplaceApply,
} from '../api/findReplaceApi';
import { FindReplaceDialogContext, FindReplaceDialogRequest } from '../hooks/useFindReplaceDialog';
import { RunInput } from '../types';
import { FindReplaceDialog } from './FindReplaceDialog';

type OpenDialog = FindReplaceDialogRequest & {
    /** Changes on every open, so the dialog starts from a clean state. */
    key: number;
};

export function FindReplaceDialogHost({ children }: { children: ReactNode }) {
    const { formatMessage } = useIntl();
    const { addJobId } = useJobSubscriptionContext();
    const [dialog, setDialog] = useState<OpenDialog | null>(null);
    const openCount = useRef(0);

    const open = useCallback((request: FindReplaceDialogRequest) => {
        openCount.current += 1;
        setDialog({ ...request, key: openCount.current });
    }, []);

    const close = useCallback(() => setDialog(null), []);

    // The server answers in English: the expected refusals get a translated sentence.
    const translateError = useCallback((e: unknown, messages: Partial<Record<number, string>>) => {
        if (e instanceof FindReplaceApiError && messages[e.status]) {
            return new Error(messages[e.status]);
        }
        return e instanceof Error ? e : new Error(String(e));
    }, []);

    const contextValue = useMemo(() => ({ open }), [open]);

    const renderDialog = (current: OpenDialog) => {
        const onScan = async (input: RunInput) => {
            try {
                const { runId, jobId } = await createFindReplaceRun(input);
                addJobId(jobId, {
                    outcomeAction: {
                        label: formatMessage({
                            id: 'findReplace.snackbar.openPreview',
                            defaultMessage: 'Open preview',
                        }),
                        onClick: () => open({ runId }),
                    },
                });
                return runId;
            } catch (e) {
                throw translateError(e, {
                    403: formatMessage({
                        id: 'findReplace.error.scanForbidden',
                        defaultMessage: 'You cannot edit the branch the scope is limited to.',
                    }),
                    404: formatMessage({
                        id: 'findReplace.error.scanNodeMissing',
                        defaultMessage: 'The branch the scope is limited to no longer exists.',
                    }),
                    409: formatMessage({
                        id: 'findReplace.error.scanConflict',
                        defaultMessage:
                            'A bulk change is already running on this hierarchy: wait for it to finish.',
                    }),
                });
            }
        };

        const onApply = async (runId: string, reapprovePublish: boolean) => {
            try {
                const { jobId } = await startFindReplaceApply(runId, reapprovePublish);
                addJobId(jobId, {
                    outcomeAction: {
                        label: formatMessage({
                            id: 'findReplace.snackbar.openResult',
                            defaultMessage: 'Open result',
                        }),
                        onClick: () => open({ runId }),
                    },
                });
                return jobId;
            } catch (e) {
                throw translateError(e, {
                    409: formatMessage({
                        id: 'findReplace.error.applyConflict',
                        defaultMessage:
                            'This preview can no longer be applied: it expired or is already being applied.',
                    }),
                });
            }
        };

        const onCancel = async (runId: string) => {
            try {
                await cancelFindReplaceRun(runId);
            } catch (e) {
                throw translateError(e, {
                    409: formatMessage({
                        id: 'findReplace.error.cancelConflict',
                        defaultMessage: 'There is nothing left to cancel.',
                    }),
                });
            }
        };

        return (
            <FindReplaceDialog
                key={current.key}
                open
                onClose={close}
                request={current}
                onScan={onScan}
                onApply={onApply}
                onCancel={onCancel}
            />
        );
    };

    return (
        <FindReplaceDialogContext.Provider value={contextValue}>
            {children}
            {dialog && renderDialog(dialog)}
        </FindReplaceDialogContext.Provider>
    );
}
